export type PeriodPreset =
  | "today"
  | "last7"
  | "last30"
  | "last90"
  | "thisMonth";

export interface DateRange {
  dataInicial: Date | null;
  dataFinal: Date | null;
}

export const PERIOD_PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: "today", label: "Hoje" },
  { key: "last7", label: "7 dias" },
  { key: "last30", label: "30 dias" },
  { key: "last90", label: "90 dias" },
  { key: "thisMonth", label: "Este mês" }
];

export function startOfDay(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function presetRange(preset: PeriodPreset): DateRange {
  const today = startOfDay(new Date());

  switch (preset) {
    case "today":
      return { dataInicial: today, dataFinal: today };
    case "last7":
      return { dataInicial: addDays(today, -6), dataFinal: today };
    case "last90":
      return { dataInicial: addDays(today, -89), dataFinal: today };
    case "thisMonth":
      return {
        dataInicial: new Date(today.getFullYear(), today.getMonth(), 1),
        dataFinal: today
      };
    default:
      return { dataInicial: addDays(today, -29), dataFinal: today };
  }
}

export function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return a === b;
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

// Descobre qual atalho corresponde ao período atual para destacar o botão,
// inclusive quando as datas foram escolhidas manualmente nos calendários.
export function matchingPreset(range: DateRange): PeriodPreset | null {
  if (!range.dataInicial || !range.dataFinal) return null;

  const preset = PERIOD_PRESETS.find(({ key }) => {
    const candidate = presetRange(key);
    return (
      isSameDay(candidate.dataInicial, range.dataInicial) &&
      isSameDay(candidate.dataFinal, range.dataFinal)
    );
  });

  return preset?.key ?? null;
}

// Quantidade de dias do período considerando as duas pontas.
export function rangeInDays(range: DateRange): number {
  if (!range.dataInicial || !range.dataFinal) return 0;

  const diff =
    startOfDay(range.dataFinal).getTime() -
    startOfDay(range.dataInicial).getTime();

  return Math.round(diff / (1000 * 60 * 60 * 24)) + 1;
}

// Diferença entre as pontas no mesmo critério do servidor ao validar o limite
// de exportação em lote: o dia inicial não é contado.
export function rangeDiffInDays(range: DateRange): number {
  return Math.max(rangeInDays(range) - 1, 0);
}

export function formatDisplayDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
}

// Formato aceito pela API nos parâmetros dataInicial/dataFinal.
export function formatApiDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}
